// src/pages/phone-numbers.tsx

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { SiteHeader } from '@/components/site-header';
import { Combobox } from '@/components/ui/combobox';
import {
    Phone,
    AlertCircle,
    Search,
    SearchX,
    Plus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ImportPhoneNumberDialog } from './components/import-phone-number-dialog';
import { getPhoneNumbers } from '@/api/phone-numbers/phone_numbers.api';
import type { PhoneNumberDetails } from '@/api/phone-numbers/phone_numbers.types';
import { useEnhancedToast } from "@/components/ui/enhanced-toast"
import { PhoneNumberCardSkeleton } from './components/PhoneNumberCardSkeleton';
import { PhoneNumberCard } from './components/PhoneNumberCard';
import { containerVariants, itemVariants } from '@/components/animations';

// Connection status options
const CONNECTION_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'connected', label: 'Connected' },
    { value: 'not_connected', label: 'Not Connected' },
];

// Provider options
const PROVIDER_OPTIONS = [
    { value: 'all', label: 'All Providers' },
    { value: 'twilio', label: 'Twilio' },
    { value: 'byo-phone-number', label: 'SIP' },
];

const PhoneNumbers = () => {
    const [numbers, setNumbers] = useState<PhoneNumberDetails[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [connectionFilter, setConnectionFilter] = useState('all');
    const [providerFilter, setProviderFilter] = useState('all');
    const navigate = useNavigate();
    const { error } = useEnhancedToast();
    const errorRef = useRef(error);

    // Update ref when error function changes
    useEffect(() => {
        errorRef.current = error;
    }, [error]);

    const fetchPhoneNumbers = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getPhoneNumbers();
            setNumbers(response.data ?? []);
        } catch (err) {
            if (err instanceof Error) {
                errorRef.current({
                    icon: AlertCircle,
                    title: "Error",
                    description: err.message || "Failed to fetch phone numbers",
                    duration: 4000,
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, []); // No dependencies needed since we're using ref

    useEffect(() => {
        fetchPhoneNumbers();
    }, [fetchPhoneNumbers]);

    // Filter phone numbers based on search and filters
    const filteredNumbers = useMemo(() => {
        return numbers.filter((number) => {
            // Search filter
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch = 
                number.name.toLowerCase().includes(searchLower) ||
                number.number.toLowerCase().includes(searchLower) ||
                (number.assistantId && number.assistantId.toLowerCase().includes(searchLower));

            // Connection filter
            const matchesConnection = 
                connectionFilter === 'all' ||
                (connectionFilter === 'connected' && number.assistantId) ||
                (connectionFilter === 'not_connected' && !number.assistantId);

            // Provider filter
            const matchesProvider = 
                providerFilter === 'all' ||
                number.provider === providerFilter;

            return matchesSearch && matchesConnection && matchesProvider;
        });
    }, [numbers, searchQuery, connectionFilter, providerFilter]);

    const hasActiveFilters = searchQuery || connectionFilter !== 'all' || providerFilter !== 'all';
    const showEmptyState = !isLoading && numbers.length === 0;
    const showNoResults = !isLoading && numbers.length > 0 && filteredNumbers.length === 0;

    const clearFilters = () => {
        setSearchQuery('');
        setConnectionFilter('all');
        setProviderFilter('all');
    };

    return (
        <div className="h-full flex flex-col">
            <SiteHeader 
                title="Phone Numbers"
                subtitle="Manage phone numbers for voice assistants"
            >
                {!showEmptyState && (
                    <>
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                placeholder="Search by name, number, or assistant ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-[250px] lg:w-[350px] pl-9"
                            />
                        </div>

                        {/* Connection Filter */}
                        <Combobox
                            options={CONNECTION_OPTIONS}
                            value={connectionFilter}
                            onValueChange={setConnectionFilter}
                            placeholder="Filter by status..."
                            className="w-[160px]"
                        />

                        {/* Provider Filter */}
                        <Combobox
                            options={PROVIDER_OPTIONS}
                            value={providerFilter}
                            onValueChange={setProviderFilter}
                            placeholder="Filter by provider..."
                            className="w-[160px]"
                        />

                        {/* Import Button */}
                        <ImportPhoneNumberDialog onRefresh={fetchPhoneNumbers} />
                    </>
                )}
            </SiteHeader>

            <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex-1 overflow-auto px-4 py-6 md:px-8 md:py-8"
            >
                {isLoading ? (
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {[...Array(6)].map((_, index) => (
                            <PhoneNumberCardSkeleton key={index} />
                        ))}
                    </motion.div>
                ) : showEmptyState ? (
                    <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 200px)' }}>
                        <EmptyState
                            icon={Phone}
                            title="No Phone Numbers Yet"
                            description="Import your existing phone numbers from Twilio or SIP to connect them with your voice assistants."
                            action={
                                <ImportPhoneNumberDialog 
                                    onRefresh={fetchPhoneNumbers}
                                    triggerButton={
                                        <Button className="gap-2">
                                            <Plus className="h-4 w-4" />
                                            Import Phone Number
                                        </Button>
                                    }
                                />
                            }
                        />
                    </div>
                ) : showNoResults ? (
                    <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 200px)' }}>
                        <div className="text-center space-y-4">
                            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                <SearchX className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">No Matching Phone Numbers</h3>
                            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                No phone numbers match your current search and filter criteria. Try adjusting your filters.
                            </p>
                            <Button variant="outline" size="sm" onClick={clearFilters}>
                                Clear All Filters
                            </Button>
                        </div>
                    </div>
                ) : (
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {filteredNumbers.map((number) => (
                            <PhoneNumberCard
                                key={number.id}
                                phoneNumber={number}
                                onClick={() => navigate(`/dashboard/phone-numbers/${number.id}`)}
                                variants={itemVariants}
                            />
                        ))}
                    </motion.div>
                )}
            </motion.div>
        </div>
    );
};

export default PhoneNumbers;
